import {
  ActionManager,
  Color3,
  CubeTexture,
  Engine,
  ExecuteCodeAction,
  HighlightLayer,
  InstancedMesh,
  Matrix,
  Mesh,
  Scene,
  SceneLoader,
} from 'babylonjs';
import 'babylonjs-loaders';

const Skyboxes = ['https://assets.babylonjs.com/environments/environmentSpecular.env'];
const glbFile = 'PREREGLAGE_FULL_ASSEMBLAGE.glb';
const materials = ['gold', 'silver', 'bronze','steel', 'titanium', 'black_plastic', 'leather', 'green_glass', 'led'];

const glassMaterial = (scene) => {
  const glass = new BABYLON.PBRMaterial("glass", scene);
  glass.indexOfRefraction = 0.52;
  glass.alpha = 0.5;
  glass.directIntensity = 0.0;
  glass.environmentIntensity = 0.7;
  glass.cameraExposure = 0.66;
  glass.cameraContrast = 1.66;
  glass.microSurface = 1;
  glass.reflectivityColor = new BABYLON.Color3(0.2, 0.2, 0.2);
  glass.albedoColor = new BABYLON.Color3(0.95, 0.95, 0.95);
  return glass
}

const goldMaterial = (scene) => {
  // CorrugatedSteel002_2K-JPG_Roughness.jpg
  const roughnessTexture = new BABYLON.Texture("/materials/gold/Metal048A_2K-JPG_Roughness.jpg", scene);
  roughnessTexture.name = "roughness";
  // CorrugatedSteel002_2K-JPG_NormalGL.jpg
  const normalTexture = new BABYLON.Texture("/materials/gold/Metal048A_2K-JPG_NormalGL.jpg", scene);
  normalTexture.name = "normal";
  // CorrugatedSteel002_2K-JPG_Color.jpg
  const colorTexture = new BABYLON.Texture("/materials/gold/Metal048A_2K-JPG_Color.jpg", scene);
  colorTexture.name = "color";


  const goldMaterial = new BABYLON.PBRMaterial("true_gold");
  goldMaterial.metallic = 1;
  goldMaterial.roughness = 1;
  goldMaterial.albedoTexture = colorTexture;
  goldMaterial.microSurfaceTexture = roughnessTexture;
  goldMaterial.bumpTexture = normalTexture;

  // ground.applyDisplacementMap("https://i.imgur.com/K5sYli2.jpg",0,0.025, undefined, new BABYLON.Vector2(0, 0.25));


  goldMaterial.invertNormalMapX = true;
  goldMaterial.invertNormalMapY = true;

  return goldMaterial
}

const diamondMaterial = (scene) => {
  const diamond = new BABYLON.PBRMaterial("diamond", scene);
  diamond.reflectivityColor = new BABYLON.Color3(0.9, 0.9, 0.9); // High reflectivity
  diamond.albedoColor = new BABYLON.Color3(1, 1, 1); // Base color of the diamond
  diamond.refractionTexture = new BABYLON.Texture("textures/env.dds", scene); // Environment texture for reflections
  diamond.indexOfRefraction = 2.417; // IOR of diamond
  diamond.alpha = 0.9; // Slight transparency
  diamond.subSurface.isRefractionEnabled = true; // Enable refraction
  diamond.subSurface.refractionIntensity = 0.9;
  return diamond
}

const emeraldMaterial = (scene) => {
  const emerald = diamondMaterial(scene);
  emerald.name = "emerald";
  emerald.emissiveColor = new BABYLON.Color3(0.0, 0.3, 0.0);
  return emerald
}

const rubyMaterial = (scene) => {
  const emerald = diamondMaterial(scene);
  emerald.name = "ruby";
  emerald.emissiveColor = new BABYLON.Color3(0.3, 0.0, 0.0);
  return emerald
}

const sapphireMaterial = (scene) => {
  const emerald = diamondMaterial(scene);
  emerald.name = "sapphire";
  emerald.emissiveColor = new BABYLON.Color3(0.0, 0.0, 0.3);
  return emerald
}

const greenGlassMaterial = (scene) => {
  const glass = new BABYLON.PBRMaterial("green_glass", scene);
  glass.indexOfRefraction = 0.52;
  glass.alpha = 0.9;
  glass.directIntensity = 0.0;
  glass.environmentIntensity = 0.7;
  glass.cameraExposure = 0.66;
  glass.cameraContrast = 1.66;
  glass.microSurface = 1;
  glass.reflectivityColor = new BABYLON.Color3(0.2, 0.2, 0.2);
  glass.albedoColor = new BABYLON.Color3(0.95, 0.95, 0.95);
  return glass
}

export default class Viewer {
  light;

  camera;

  scene;

  engine;

  canvas;

  highlightLayer;

  options;

  selectedObjects;

  glbFile;

  canSelect = false;

  allMeshes = [];

  materials = [];

  constructor(canvas, options, file) {
    this.canvas = canvas;
    this.options = options;
    this.selectedObjects = [];
    this.glbFile = file || glbFile;
    this.init();
  }

  init() {
    // Créer une scène BabylonJS
    this.engine = new Engine(this.canvas, true, { stencil: true });
    this.scene = new Scene(this.engine);

    this.initLight();

    this.loadSkybox();
    this.initHighlight();

    this.scene.pointerMovePredicate = (mesh) => mesh.isPickable && mesh.isVisible && mesh.isReady() && mesh.isEnabled() && this.canSelect;

    this.scene.onPointerUp = () => {
      if (this.canSelect) {
        const ray = this.scene.createPickingRay(this.scene.pointerX, this.scene.pointerY, Matrix.Identity(), this.camera);
        const hit = this.scene.pickWithRay(ray);
        if (hit.pickedMesh) {
          const existedObject = this.selectedObjects.find((o) => o.mesh.id === hit.pickedMesh.id);
          if (existedObject) {
            this.selectedObjects = this.selectedObjects.filter((o) => o.mesh.id !== existedObject.mesh.id);
            this.removeHighlight(existedObject.mesh);
          } else {
            this.selectedObjects.push({
              mesh: hit.pickedMesh,
              albedoColor: hit.pickedMesh.material.albedoColor,
            });
            this.highlight(hit.pickedMesh);
          }
        }
      }
    };

    // Charger le fichier glb
    SceneLoader.ImportMesh('', 'models/', this.glbFile, this.scene, (meshes) => {
      const asyncLoadMaterials = this.loadMaterials();
      this.materials.push(glassMaterial(this.scene));
      this.materials.push(greenGlassMaterial(this.scene));
      this.materials.push(goldMaterial(this.scene));
      this.materials.push(diamondMaterial(this.scene));
      this.materials.push(emeraldMaterial(this.scene));
      this.materials.push(rubyMaterial(this.scene));
      this.materials.push(sapphireMaterial(this.scene));
      this.createCamera();
      this.configureRightClickSensibility();
      meshes.forEach((mesh) => {
        const newMesh = this.transformToMesh(mesh);
        // this.createHoverMesh(newMesh);
        this.allMeshes.push(newMesh);
      });

      // Lancer le rendu de la scène
      this.engine.runRenderLoop(() => {
        this.scene.render();
      });

      // Redimensionner le canvas lorsque la fenêtre est redimensionnée
      window.addEventListener('resize', () => {
        this.engine.resize();
      });
      if (this.options.onLoad) {
        this.options.onLoad(this.allMeshes)
      }
      if (this.options.onLoadMaterials) {
        asyncLoadMaterials.then(() => this.options.onLoadMaterials());
      }
      if (this.options.showDebugger) {
        this.showDebugger();
      }
    });
  }

  loadMaterials() {
    return Promise.all(
      materials.map(mat => {
        return BABYLON.loadAssetContainerAsync(`materials/mat_${mat}.glb`, this.scene).then((container) => {
          const importedMaterial = container.materials[0];
          importedMaterial.needDepthPrePass = true;
          this.materials.push(importedMaterial);
        });
      }))
  }

  initLight() {
    // Créer une lumière
    this.light = this.scene.createDefaultLight();
  }

  initHighlight() {
    this.highlightLayer = new HighlightLayer('highlightLayer', this.scene);
    this.highlightLayer.innerGlow = true;
  }

  // eslint-disable-next-line class-methods-use-this
  transformToMesh(mesh) {
    if (mesh instanceof InstancedMesh) {
      const newMesh = mesh.sourceMesh.clone(`${mesh.name}blink_tmp`, mesh.parent);
      newMesh.position = mesh.position.clone();
      if (mesh.rotationQuaternion) newMesh.rotationQuaternion = mesh.rotationQuaternion.clone();
      newMesh.scaling = mesh.scaling.clone();
      mesh.dispose();
      return newMesh; 
    }
    return mesh;
  }

  createHoverMesh(mesh) {
    if (!mesh.getChildMeshes().length) {
      mesh.actionManager = new ActionManager(this.scene);
      mesh.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPointerOverTrigger, (e) => {
        if (e.source instanceof Mesh && this.canSelect) {
          this.highlight(e.source);
        }
      }));
      mesh.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPointerOutTrigger, (e) => {
        if (e.source instanceof Mesh && !this.selectedObjects.some((o) => o.mesh.uniqueId === e.source.uniqueId)) {
          this.highlightLayer.removeMesh(e.source);
        }
      }));
    }
  }

  configureRightClickSensibility() {
    this.scene.activeCamera.panningSensibility = 50000;
  }

  createCamera() {
    if (!this.scene.activeCamera) {
      this.scene.createDefaultCamera(true);

      this.camera = this.scene.activeCamera;
      this.camera.alpha += Math.PI;

      // Enable camera's behaviors
      this.camera.useFramingBehavior = true;

      const framingBehavior = this.camera.getBehaviorByName('Framing');
      framingBehavior.framingTime = 0;
      framingBehavior.elevationReturnTime = -1;

      if (this.scene.meshes.length) {
        this.camera.lowerRadiusLimit = null;

        const worldExtends = this.scene.getWorldExtends((mesh) => mesh.isVisible && mesh.isEnabled());
        framingBehavior.zoomOnBoundingInfo(worldExtends.min, worldExtends.max);
      }

      this.camera.pinchPrecision = 200 / this.camera.radius;
      this.camera.upperRadiusLimit = 5 * this.camera.radius;

      this.camera.wheelDeltaPercentage = 0.01;
      this.camera.pinchDeltaPercentage = 0.01;

      this.camera.alpha = 4.9738;
      this.camera.beta = 1.6248;
    }

    this.scene.activeCamera.attachControl();
  }

  loadSkybox() {
    const path = Skyboxes[0];
    this.scene.environmentTexture = CubeTexture.CreateFromPrefilteredData(path, this.scene);
    // this.scene.environmentTexture = new CubeTexture(path, this.scene);
    // this.scene.createDefaultSkybox(new CubeTexture(path, this.scene), false, 1000, 0.1);

  }

  showDebugger() {
    this.scene.debugLayer.show();
  }

  setColorToSelectedObjects(color) {
    this.selectedObjects.forEach((obj) => {
      const newMaterial = obj.mesh.material.clone();
      newMaterial.albedoColor = Color3.FromHexString(color);
      obj.mesh.material = newMaterial;
    });
  }

  highlight(mesh) {
    this.highlightLayer.addMesh(mesh, Color3.Green());
  }

  removeHighlight(mesh) {
    this.highlightLayer.removeMesh(mesh);
  }

  toggleHighlight(mesh) {
    if (this.highlightLayer.hasMesh(mesh)) {
      this.removeHighlight(mesh);
    } else {
      this.highlight(mesh);
    }
  }

  clearSelectedObjects() {
    this.selectedObjects = [];
  }

  getSelectedObjects() {
    return this.selectedObjects;
  }

  setSelectedObjects(objects) {
    this.selectedObjects = objects;
  }

  clearMaterialSelectedObjects() {
    this.selectedObjects.forEach((obj) => {
      const newMaterial = obj.mesh.material.clone();
      newMaterial.albedoColor = obj.albedoColor;
      obj.mesh.material = newMaterial;
    });
  }

  clearHighlightObjects() {
    this.selectedObjects.forEach((obj) => {
      this.highlightLayer.removeMesh(obj.mesh);
    });
  }

  setObjectSelector(canSelect) {
    this.canSelect = canSelect;
  }

  getAllMeshes() {
    return this.allMeshes;
  }

  setColorForIds(ids, color) {
    const selectedMeshes = this.allMeshes.filter((o) => ids.includes(o.uniqueId));

    if(selectedMeshes.length) {
      selectedMeshes.forEach((mesh) => {
        const newMaterial = mesh.material.clone();
        newMaterial.albedoColor = Color3.FromHexString(color);
        mesh.material = newMaterial;
      })
    }
  }

  setMaterialForIds(ids, materialName) {
    const selectedMaterial = this.materials.find((m) => m.name === materialName);
    const selectedMeshes = this.allMeshes.filter((o) => ids.includes(o.uniqueId));

    if(selectedMeshes.length && selectedMaterial) {
      selectedMeshes.forEach((mesh) => {
        mesh.material = selectedMaterial;
      });
    }
  }

  hideMeshes(ids) {
    this.setMeshesVisibility(ids, false);
  }

  showMeshes(ids) {
    this.setMeshesVisibility(ids, true);
  }

  setMeshesVisibility(ids, isVisible) {
    if(ids && ids.length) {
      const selectedMeshes = this.allMeshes.filter((o) => ids.includes(o.uniqueId));
      selectedMeshes.forEach((mesh) => {
        mesh.isVisible = isVisible;
      });
    }
  }

}
